import { supabase } from "@/integrations/supabase/client";

/**
 * Lightweight analytics for the search bar so we can see which alias terms
 * users type and whether they actually click through to a compound page.
 *
 * Usage:
 *   - logSearchImpression on every committed query (fired debounced from the
 *     search bar) — captures the query, what we matched, and result count.
 *   - logSearchClick when the user picks a compound result — flips
 *     `clicked = true` on the most recent matching impression.
 *
 * Failures are swallowed: this is fire-and-forget telemetry.
 */

const SESSION_KEY = "alias_search_session";

function sessionId(): string {
  try {
    let s = sessionStorage.getItem(SESSION_KEY);
    if (!s) {
      s = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
      sessionStorage.setItem(SESSION_KEY, s);
    }
    return s;
  } catch {
    return "anon";
  }
}

export function normalizeQuery(q: string): string {
  return q.toLowerCase().replace(/\s+/g, " ").trim();
}

export interface ImpressionInput {
  query: string;
  matchedCompoundSlug?: string | null;
  matchedAlias?: string | null;
  matchType?: "name" | "alias_exact" | "alias_prefix" | "alias_substring" | "name_prefix" | "name_substring" | "none";
  resultCount: number;
}

let lastQueryNorm = "";
let lastSent = 0;

export async function logSearchImpression(input: ImpressionInput) {
  const norm = normalizeQuery(input.query);
  if (norm.length < 2) return;
  const now = Date.now();
  // Debounce identical consecutive queries within 1.5s
  if (norm === lastQueryNorm && now - lastSent < 1500) return;
  lastQueryNorm = norm;
  lastSent = now;

  try {
    await (supabase.from("alias_search_events" as any) as any).insert({
      query: input.query.slice(0, 200),
      query_normalized: norm.slice(0, 200),
      matched_compound_slug: input.matchedCompoundSlug ?? null,
      matched_alias: input.matchedAlias ?? null,
      match_type: input.matchType ?? "none",
      result_count: input.resultCount,
      session_id: sessionId(),
    });
  } catch {
    // ignore — telemetry should never break the UI
  }
}

export async function logSearchClick(query: string, clickedCompoundSlug: string) {
  const norm = normalizeQuery(query);
  if (norm.length < 2) return;
  try {
    // Mark the most recent impression for this session+query as clicked.
    const { data } = await (supabase.from("alias_search_events" as any) as any)
      .select("id")
      .eq("session_id", sessionId())
      .eq("query_normalized", norm)
      .order("created_at", { ascending: false })
      .limit(1);
    const eventId = (data && (data as any[])[0]?.id) as string | undefined;
    if (eventId) {
      await (supabase.from("alias_search_events" as any) as any)
        .update({ clicked: true, clicked_compound_slug: clickedCompoundSlug })
        .eq("id", eventId);
    } else {
      // Fallback: write a standalone "click" row
      await (supabase.from("alias_search_events" as any) as any).insert({
        query: query.slice(0, 200),
        query_normalized: norm.slice(0, 200),
        result_count: 0,
        clicked: true,
        clicked_compound_slug: clickedCompoundSlug,
        session_id: sessionId(),
      });
    }
  } catch {
    // ignore
  }
}
